// ante-react-kit/src/components/v2/AntHeaderCard/SummaryPanel.js
import React, { useMemo } from 'react';
import AntLocalization from '../../../utils/AntLocalization';
import AntCondition from '../../../utils/AntCondition';
import AntConfig from '../../../utils/AntConfig';
import { AntGlobal } from '../../../utils/AntContext';
import { getStatusTheme } from './StatusTheme';

var AMOUNT_COLORS = {
    total_credit_payable: 'payable',
    total_credit_refundable: 'payable',
    total_credit_paid: 'paid',
    total_credit_refund: 'paid',
    total_gross_price: 'highlight'
};

function formatNumber(value) {
    if (value === null || value === undefined || value === '') return '—';
    var num = Number(value);
    if (isNaN(num)) return String(value);
    return num.toLocaleString('vi-VN');
}

export default function SummaryPanel(props) {
    var { fields, items, formData, table, status } = props;
    var theme = getStatusTheme(status);
    var appGlobal = AntGlobal();

    // Build label map from AntConfig (FE field configs with labels)
    var labelMap = useMemo(function () {
        var map = {};

        // 1. Load from AntConfig (config/fields/AROrder.json) — has translated labels
        if (table && appGlobal) {
            var config = new AntConfig(appGlobal, table);
            var cnfFields = config.loadFields(table);
            if (cnfFields && cnfFields.length > 0) {
                cnfFields.forEach(function (cnf) {
                    if (cnf.dataField && cnf.label) {
                        map[cnf.dataField] = AntLocalization.translateText(cnf.label);
                    }
                });
            }
        }

        // 2. Override from items (header-items.json) — inline labels have higher priority
        if (items) {
            items.forEach(function (item) {
                if (item.dataField && item.label) {
                    map[item.dataField] = AntLocalization.translateText(item.label);
                }
                // cnfField alias (e.g. totalPrice uses label from total_revenue_billable_amt)
                if (item.dataField && item.cnfField && !map[item.dataField]) {
                    if (map[item.cnfField]) {
                        map[item.dataField] = map[item.cnfField];
                    }
                }
            });
        }

        return map;
    }, [items, table, appGlobal]);

    // Build fieldConfigs map from items for visibility checks
    var fieldConfigs = useMemo(function () {
        if (!items) return {};
        var map = {};
        items.forEach(function (item) {
            if (item.dataField) map[item.dataField] = item;
        });
        return map;
    }, [items]);

    // Resolve visible items
    var visibleItems = useMemo(function () {
        if (!fields || !formData) return [];
        return fields
            .filter(function (fieldName) {
                var cnf = fieldConfigs[fieldName];
                if (cnf && cnf.visible !== undefined) {
                    if (cnf.visible === false) return false;
                    if (typeof cnf.visible === 'object') {
                        return AntCondition.isVisible(formData, cnf);
                    }
                }
                // Also check visibleSelf from AntConfig
                return true;
            })
            .map(function (fieldName) {
                var label = labelMap[fieldName] || fieldName;
                var value = formData[fieldName];
                var colorKey = AMOUNT_COLORS[fieldName] || null;
                return { fieldName: fieldName, label: label, value: value, colorKey: colorKey };
            })
            .filter(function (item) {
                // Hide items with null/undefined/empty value
                if (item.value === null || item.value === undefined || item.value === '') return false;
                return true;
            });
    }, [fields, formData, fieldConfigs, labelMap]);

    var isStrikethrough = theme.summaryFontScale === 'strikethrough';
    var isCompact = theme.summaryFontScale === 'compact';

    if (visibleItems.length === 0) return null;

    return (
        <div className={'v2-summary-panel' + (isCompact ? ' v2-summary-panel--compact' : '')}>
            {visibleItems.map(function (item) {
                var valueClass = 'v2-summary-panel__value';
                if (item.colorKey === 'payable' && Number(item.value) > 0) valueClass += ' v2-summary-panel__value--payable';
                if (item.colorKey === 'paid') valueClass += ' v2-summary-panel__value--paid';
                if (item.colorKey === 'highlight') valueClass += ' v2-summary-panel__value--highlight';
                if (isStrikethrough) valueClass += ' v2-summary-panel__value--strikethrough';

                var isStatus = item.fieldName === 'mix_status' || item.fieldName === 'order_status';

                return (
                    <div key={item.fieldName} className="v2-summary-panel__row">
                        <span className="v2-summary-panel__label">{item.label}</span>
                        {isStatus ? (
                            <span className="v2-summary-panel__status">{item.value}</span>
                        ) : (
                            <span className={valueClass}>{formatNumber(item.value)}</span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
